import { ROLE_LABELS, listaEs, type Role } from '@ops/contracts'
import type { ReactNode } from 'react'

import { Card } from './Card'
import { Icon } from './Icon'

/**
 * Estado de acceso denegado de una sección entera.
 *
 * Vive en `src/ui/` y no en un módulo porque lo necesitan dos lugares distintos
 * y tienen que decir lo mismo: la **guarda de ruta** (alguien entró por URL a
 * una pantalla que su rol no ve) y el **módulo** cuando la API le devuelve 403.
 * Antes existía una sola copia dentro de Revenue, que era el único módulo que la
 * había escrito; con seis rutas guardadas, dos textos distintos para el mismo
 * hecho sería un bug de producto.
 *
 * Lo que este componente **no** es: seguridad. El permiso lo aplica la API
 * (`requireRole`) y el recorte de $$ pasa en el servidor (`stripMoney`). Esto
 * explica una negativa que ya ocurrió.
 */
export interface AccessDeniedProps {
  /** Nombre de la sección tal como se lee en la navegación, ej. "Revenue". */
  seccion: string
  /** Roles que sí entran. Se enumeran en prosa: "Admin y Comercial". */
  permitidos: readonly Role[]
  /** Rol del usuario, cuando se lo pudo resolver. */
  rol?: Role
  /** Mensaje del backend cuando el 403 vino de la API. Gana sobre el propio: es
   *  el que dice qué pasó de verdad. */
  mensaje?: string
  /** Por qué existe la restricción en esta sección puntual. */
  detalle?: ReactNode
}

export function AccessDenied({
  seccion,
  permitidos,
  rol,
  mensaje,
  detalle,
}: AccessDeniedProps) {
  const quienes = listaEs(permitidos.map((permitido) => ROLE_LABELS[permitido]))

  return (
    <Card
      as="section"
      ariaLabel={`Sin acceso a ${seccion}`}
      padding="lg"
      severidad="critical"
      className="max-w-prosa"
    >
      {/* h2 y no h1: el h1 de la página lo pone el header del shell. */}
      <h2 className="mb-1.5 inline-flex items-center gap-2 text-titulo text-text">
        <Icon name="critico" size={15} className="text-danger-text" />
        {seccion} es solo para {quienes}
      </h2>
      <p className="text-meta leading-relaxed text-muted-strong">
        {mensaje ??
          (rol === undefined
            ? 'Tu cuenta no tiene permiso para ver esta sección.'
            : `Tu rol (${ROLE_LABELS[rol]}) no entra acá. El permiso lo aplica la API: esconder el link de la sidebar es cortesía, y entrar por URL igual no muestra los datos.`)}
      </p>
      {detalle}
      <p className="mt-3 text-micro text-muted-strong">
        Si necesitás un dato de esta sección para tu trabajo, pedilo por el canal
        del equipo: se evalúa exponer ese dato puntual, no la sección.
      </p>
    </Card>
  )
}
